import { useEffect, useState } from "react";
import type { CrmAccount } from "@shared/modules/crm";
import { ApiError, api } from "../../../core/lib/api";
import { Button } from "../../../core/components/ui/button";
import { Field, Input, Select } from "../../../core/components/ui/input";
import { Modal } from "../../../core/components/ui/modal";
import { useToastStore } from "../../../core/stores/toast";
import { dateTimeLocalToIso, toDateTimeLocalValue } from "../lib/crm";

type Mode = "link" | "create";

/** Le strict nécessaire de la réponse de /api/tenants : pas d'import du module tenants. */
interface TenantOption {
  id: string;
  name: string;
}

interface ActivateDialogProps {
  open: boolean;
  account: CrmAccount;
  onClose: () => void;
  onActivated: () => void;
}

/** Activation d'un dossier : liaison à un établissement existant ou création. */
export function ActivateDialog({ open, account, onClose, onActivated }: ActivateDialogProps) {
  const push = useToastStore((s) => s.push);
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [mode, setMode] = useState<Mode>("link");
  const [tenantId, setTenantId] = useState("");
  const [tenantName, setTenantName] = useState("");
  const [activationDate, setActivationDate] = useState("");
  const [saving, setSaving] = useState(false);

  // Réouverture = nouveau départ : le nom est repris du dossier, la date
  // d'activation retombe sur aujourd'hui.
  useEffect(() => {
    if (!open) return;
    setMode("link");
    setTenantId("");
    setTenantName(account.name);
    setActivationDate(toDateTimeLocalValue(Date.now()).slice(0, 10));
    api<TenantOption[]>("/api/tenants")
      .then(setTenants)
      .catch(() => setTenants([]));
  }, [open, account.name]);

  const activate = async () => {
    if (saving) return;
    if (mode === "link" && !tenantId) {
      push("danger", "Choisissez un établissement à lier");
      return;
    }
    if (mode === "create" && !tenantName.trim()) {
      push("danger", "Le nom de l'établissement est requis");
      return;
    }
    // `activatedAt` est optionnel côté schéma (jamais null) : on l'omet si vide.
    const activatedAt = dateTimeLocalToIso(activationDate ? `${activationDate}T12:00` : "");
    setSaving(true);
    try {
      await api(`/api/crm/accounts/${account.id}/activate`, {
        method: "POST",
        body: JSON.stringify(
          mode === "link"
            ? { mode, tenantId, ...(activatedAt ? { activatedAt } : {}) }
            : { mode, tenantName: tenantName.trim(), ...(activatedAt ? { activatedAt } : {}) },
        ),
      });
      push("success", "Dossier activé", "L'établissement est lié au dossier.");
      onActivated();
    } catch (e) {
      const code = e instanceof ApiError ? e.code : "";
      if (code === "already_linked") {
        push("danger", "Dossier déjà lié", "Un autre établissement est déjà rattaché à ce dossier.");
      } else if (code === "tenant_already_linked") {
        push("danger", "Établissement déjà pris", "Cet établissement est lié à un autre dossier.");
      } else if (code === "tenant_not_found") {
        push("danger", "Établissement introuvable");
      } else if (code === "tenants_module_disabled") {
        push("danger", "Module établissements désactivé");
      } else {
        push("danger", "Échec de l'activation");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Activer le dossier"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={() => void activate()} disabled={saving}>
            {saving ? "Activation…" : "Activer"}
          </Button>
        </>
      }
    >
      <div className="crm-form">
        <div className="crm-form__row">
          <label className="crm-check">
            <input
              type="radio"
              name="activate-mode"
              checked={mode === "link"}
              onChange={() => setMode("link")}
            />
            <span>Lier un établissement existant</span>
          </label>
          <label className="crm-check">
            <input
              type="radio"
              name="activate-mode"
              checked={mode === "create"}
              onChange={() => setMode("create")}
            />
            <span>Créer l'établissement</span>
          </label>
        </div>

        <div className="crm-form__grid">
          {mode === "link" ? (
            <Field
              label="Établissement"
              hint={
                tenants.length === 0 ? "Aucun établissement disponible — créez-le plutôt." : undefined
              }
            >
              <Select value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
                <option value="">Choisir…</option>
                {tenants.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </Select>
            </Field>
          ) : (
            <Field label="Nom de l'établissement">
              <Input value={tenantName} onChange={(e) => setTenantName(e.target.value)} />
            </Field>
          )}
          <Field label="Date d'activation">
            <Input
              type="date"
              value={activationDate}
              onChange={(e) => setActivationDate(e.target.value)}
            />
          </Field>
        </div>
      </div>
    </Modal>
  );
}
